<template>
  <div class="assignment-selection-modal" :class="{ 'assignment-selection-modal--nested': nested }">
    <AppModal :close-on-overlay="nested || !isMobile"
              :max-height="modalMaxHeight"
              :nested="nested"
              :show-close-button="true"
              :title="$t('general.select_assignment')"
              :visible.sync="isOpen"
              :width="modalWidth"
    >
      <div class="assignment-selection-body">
        <div class="search-section mb-3">
          <search-input v-model="searchQuery" @search="handleSearch"/>
        </div>

        <default-loading v-if="loading"></default-loading>
        <template v-else>
          <div v-if="assignmentList && assignmentList.length > 0" class="assignment-selection-table-wrap">
            <table class="uk-table uk-table-middle uk-table-divider assignment-selection-table">
              <thead>
                <tr>
                  <th>{{ $t('general.assignment_name') }}</th>
                  <th>{{ $t('general.end_date') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(assignment, index) in assignmentList" :key="index">
                  <td>{{ assignment.name }}</td>
                  <td>{{ assignment.end_date }}</td>
                  <td class="text-right">
                    <button
                      class="uk-button uk-button-default"
                      type="button"
                      @click="selectAssignment(assignment.id)"
                    >
                      {{ $t('general.assign') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <pagination v-if="paginationData" :pagination="paginationData"
                      class="mt-3 mb-2" @changePage="handlePageChange" />

          <div v-else-if="!assignmentList || assignmentList.length === 0" class="uk-alert-warning" uk-alert>
            <p>{{ $t('general.no_assignments_available') }}</p>
          </div>
        </template>
      </div>
      <template #footer>
        <button class="btn btn-danger" type="button" @click="closeModal">
          {{ $t("general.close") }}
        </button>
      </template>
    </AppModal>
  </div>
</template>

<script>
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import Pagination from "@/assets/components/Pagination";
import SearchInput from "@/view/components/inputs/SearchInput.vue";
import AppModal from "@/view/components/ui/AppModal.vue";

export default {
  name: "AssignmentSelectionModal",
  components: {
    DefaultLoading,
    Pagination,
    SearchInput,
    AppModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    nested: {
      type: Boolean,
      default: false
    },
    assignmentData: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      isMobile: false
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      }
    },
    modalWidth() {
      if (this.nested) {
        return this.isMobile ? "calc(100% - 32px)" : "800px";
      }
      return this.isMobile ? "100%" : "800px";
    },
    modalMaxHeight() {
      if (this.nested) {
        return this.isMobile ? "85vh" : "90vh";
      }
      return this.isMobile ? "100%" : "90vh";
    },
    assignmentList() {
      return this.assignmentData?.data || [];
    },
    paginationData() {
      const data = this.assignmentData;
      if (!data || typeof data !== "object" || Array.isArray(data)) {
        return null;
      }

      const currentPage = Number(data.current_page);
      const lastPage = Number(data.last_page);
      if (!Number.isFinite(currentPage) || !Number.isFinite(lastPage) || lastPage < 1) {
        return null;
      }

      return { ...data };
    }
  },
  methods: {
    updateIsMobile() {
      this.isMobile = typeof window !== "undefined" && window.innerWidth <= 767;
    },
    closeModal() {
      this.isOpen = false;
    },
    selectAssignment(assignmentId) {
      this.$emit('assignment-selected', assignmentId);
    },
    handlePageChange(page) {
      this.$emit('page-changed', page);
    },
    handleSearch(searchValue) {
      this.$emit('search-changed', searchValue);
    }
  },
  watch: {
    visible(isOpen) {
      if (isOpen) {
        this.searchQuery = '';
        return;
      }
      this.searchQuery = '';
      this.$emit('close');
    }
  },
  mounted() {
    this.updateIsMobile();
    window.addEventListener("resize", this.updateIsMobile, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateIsMobile);
  }
};
</script>

<style scoped>
.assignment-selection-body {
  min-width: 0;
}

.search-section {
  padding: 0;
}

.assignment-selection-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.assignment-selection-table {
  margin-bottom: 0;
}

.uk-button-default {
  transition: all 0.3s ease;
}

.uk-button-default:hover {
  background-color: #f8f9fa;
  transform: translateY(-1px);
}

::v-deep .pagination {
  margin-bottom: 0;
}

::v-deep .pagination .page-link {
  font-size: 14px;
  padding: 0.375rem 0.75rem;
}

@media (max-width: 767px) {
  .assignment-selection-modal:not(.assignment-selection-modal--nested) ::v-deep .app-modal-overlay:not(.app-modal-overlay--nested) {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }

  .assignment-selection-modal:not(.assignment-selection-modal--nested) ::v-deep .app-modal-overlay:not(.app-modal-overlay--nested) .app-modal {
    width: 100% !important;
    max-width: 100% !important;
    height: 100%;
    min-height: 100%;
    max-height: 100% !important;
    border-radius: 0;
    box-shadow: none;
  }

  .assignment-selection-modal:not(.assignment-selection-modal--nested) ::v-deep .app-modal-overlay:not(.app-modal-overlay--nested) .app-modal-header {
    border-radius: 0;
  }

  .assignment-selection-modal:not(.assignment-selection-modal--nested) ::v-deep .app-modal-overlay:not(.app-modal-overlay--nested) .app-modal-title {
    font-size: 16px;
    line-height: 1.4;
  }

  .assignment-selection-modal:not(.assignment-selection-modal--nested) ::v-deep .app-modal-overlay:not(.app-modal-overlay--nested) .app-modal-close {
    min-width: 48px;
    min-height: 48px;
  }

  .assignment-selection-modal:not(.assignment-selection-modal--nested) ::v-deep .app-modal-overlay:not(.app-modal-overlay--nested) .app-modal-body {
    flex: 1;
    min-height: 0;
    padding: 16px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .assignment-selection-modal:not(.assignment-selection-modal--nested) ::v-deep .app-modal-overlay:not(.app-modal-overlay--nested) .app-modal-footer {
    padding: 16px;
  }

  .assignment-selection-modal ::v-deep .app-modal-footer .btn {
    min-height: 48px;
    margin: 0;
    border-radius: 10px;
    font-size: 16px;
  }

  .assignment-selection-modal ::v-deep .app-modal-footer .btn:active {
    transform: scale(0.98);
    opacity: 0.85;
  }

  .assignment-selection-table {
    min-width: 520px;
  }

  .assignment-selection-table th,
  .assignment-selection-table td {
    white-space: nowrap;
  }
}

@media (hover: none) {
  .assignment-selection-modal ::v-deep .app-modal-footer .btn:hover,
  .assignment-selection-modal ::v-deep .app-modal-close:hover {
    opacity: 1;
    color: #666;
  }
}

@media (prefers-reduced-motion: reduce) {
  .uk-button-default:hover {
    transform: none;
  }

  .assignment-selection-modal ::v-deep .app-modal-footer .btn:active {
    transform: none;
  }
}
</style>
